import React, { Suspense } from 'react'
import {
  Outlet,
  createRootRouteWithContext,
  useLocation,
} from '@tanstack/react-router'

import type { QueryClient } from '@tanstack/react-query'
import { AuthGuard } from '@/features/auth/components/auth-guard'
import { GlobalErrorBoundary } from '@/components/common/global-error-boundary'
import { ConfirmDialogProvider } from '@/components/common/confirm-dialog-provider'
import { FormDialogProvider } from '@/components/common/form-dialog-provider'

interface MyRouterContext {
  queryClient: QueryClient
}

function RootComponent() {
  const location = useLocation()

  // List of public routes that don't require authentication
  const publicRoutes = ['/callback', '/sign-in']
  const isPublicRoute =
    publicRoutes.includes(location.pathname) ||
    location.pathname.match(/^\/invites\/[^/]+\/confirm$/)

  // Render devtools only in development, lazy-loaded to keep out of main chunks
  const Devtools = import.meta.env.DEV
    ? React.lazy(() => import('@/components/devtools/Devtools'))
    : (null as unknown as React.ComponentType)
  const devtools = import.meta.env.DEV ? (
    <Suspense fallback={null}>
      <Devtools />
    </Suspense>
  ) : null

  if (isPublicRoute) {
    return (
      <ConfirmDialogProvider>
        <FormDialogProvider>
          <GlobalErrorBoundary>
            <Outlet />
            {devtools}
          </GlobalErrorBoundary>
        </FormDialogProvider>
      </ConfirmDialogProvider>
    )
  }

  return (
    <ConfirmDialogProvider>
      <FormDialogProvider>
        <GlobalErrorBoundary>
          <AuthGuard>
            <Outlet />
            {devtools}
          </AuthGuard>
        </GlobalErrorBoundary>
      </FormDialogProvider>
    </ConfirmDialogProvider>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
})
